import { useOrderingSessionState } from '@/features/ordering/hooks/useOrderingSessionState'
import React from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'

interface OrderingSessionGuardProps {
  children: React.ReactNode
}

function SessionCheckLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background-gray dark:bg-gray-900">
      <div className="w-full max-w-md p-8">
        <div className="rounded-base border border-border-base bg-white p-8 shadow-card dark:border-gray-700 dark:bg-gray-900">
          <div className="text-center">
            <div
              className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
              aria-label="Loading"
            />
            <p className="mt-2 text-sm font-medium text-text-body dark:text-gray-400">Loading order…</p>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Keeps the editable order pages off a session that has already produced an order,
 * sending the provider to that order instead so a submitted order never reappears as
 * an unsent draft it can submit a second time.
 *
 * Must stay a route element rather than an early return inside the pages. The pages arm
 * autosave as soon as they hydrate, so a page that renders a notice after mounting has
 * already written to the session it was trying to protect.
 *
 * Only `sessionId`-bearing routes need this; a session-less URL is a brand-new order.
 */
export const OrderingSessionGuard: React.FC<OrderingSessionGuardProps> = ({ children }) => {
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('sessionId')?.trim()
  const session = useOrderingSessionState(sessionId)

  if (!sessionId) {
    return <>{children}</>
  }

  switch (session.status) {
    case 'idle':
    case 'loading':
      return <SessionCheckLoading />
    case 'open':
      return <>{children}</>
    case 'submitted':
      return <Navigate to={`/order/in-progress/${encodeURIComponent(session.orderId)}`} replace />
    // A missing session and a failed probe both fall through to the page, which already
    // resolves them per launch flow: it shows Epic its own terminal screen and sends a
    // standalone provider to the workspace. Redirecting here would strand an Epic
    // provider on a route their launch cannot authenticate. Submitting is still blocked
    // server-side, so passing through cannot resubmit anything.
    case 'gone':
    case 'error':
      return <>{children}</>
    default: {
      const _exhaustiveStatus: never = session
      void _exhaustiveStatus
      return <SessionCheckLoading />
    }
  }
}

export default OrderingSessionGuard
